import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '@numa-group/ui.modal';
import { useRouter as useNextRouter } from 'next/router';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { SearchDates } from './components/search-dates/search-dates';
import { SearchGuests } from './components/search-guests/search-guests';
import { SearchLocation } from './components/search-location/search-location';
import { SearchSchema, searchSchema } from './search.schema';
import { useBottomNavigation } from '../../bottom-navigation.context';
import { WithCustomRouter } from '../../types';
import { Link, LinkProps } from '../link/link';

export type SearchProps = WithCustomRouter<
  {
    cities: Array<{
      cityName: string;
      enCityName: string;
      slug: string;
    }>;
    href: URL | string;
  } & Pick<LinkProps, 'isActive'>
>;

export const Search = ({ cities, href, isActive, useRouter = useNextRouter }: SearchProps) => {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const { content } = useBottomNavigation();
  const [step, setStep] = useState<'location' | 'dates' | 'guests'>('location');
  const [open, setOpen] = useState(false);
  const searchForm = useForm<SearchSchema>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      guests: {
        adults: 1,
        children: 0,
        infants: 0,
      },
    },
  });

  const guests = searchForm.watch('guests');

  const redirectToSearchPage = async ({ location, dates, guests }: SearchSchema) => {
    const city = cities.find(({ slug }) => slug === location)!.enCityName;
    const query = {
      arrival: dates.arrival,
      departure: dates.departure,
      location: city,
      adults: guests.adults.toString(),
      children: guests.children.toString(),
      infants: guests.infants.toString(),
    };

    if (href instanceof URL) {
      const searchUrlWithParams = new URL(href);
      for (const [key, value] of Object.entries(query)) {
        searchUrlWithParams.searchParams.set(key, value);
      }
      window.location.href = searchUrlWithParams.href;
      return;
    }

    await router.push({
      pathname: href,
      query,
    });
    handleOpenChange(false);
  };

  const onSuccess = () => redirectToSearchPage(searchForm.getValues());
  const onError = () => {
    console.error(searchForm.formState.errors);
  };
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);

    if (!isOpen) {
      setStep('location');
      searchForm.reset();
    }
  };

  const numberOfGuests = Object.values(guests).reduce((acc, value) => acc + value, 0) || 1;

  const title: Record<typeof step, string> = {
    dates: content.dateSelectionTitle,
    guests: formatMessage({ id: 'guests', defaultMessage: content.guests }, { numberOfGuests }),
    location: content.location,
  };

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <Modal.Trigger>
        <Link icon="search" isActive={!!isActive}>
          {content.search}
        </Link>
      </Modal.Trigger>

      <Modal.Header>
        {step !== 'location' && <Modal.Back onBack={() => setStep(step === 'dates' ? 'location' : 'dates')} />}
        <Modal.Title>{title[step]}</Modal.Title>
        <Modal.Close />
      </Modal.Header>

      <FormProvider {...searchForm}>
        <Modal.Form onSubmit={searchForm.handleSubmit(onSuccess, onError)}>
          {step === 'location' && <SearchLocation locations={cities} onSelect={() => setStep('dates')} />}
          {step === 'dates' && <SearchDates cities={cities} onSelect={() => setStep('guests')} />}
          {step === 'guests' && <SearchGuests isSubmitting={searchForm.formState.isSubmitting} />}
        </Modal.Form>
      </FormProvider>
    </Modal>
  );
};
